import { shuffle } from "lodash";
import { useMemo } from "react";

import useHasAccessToExamQuestions from "seneca-common/features/exam-questions/hooks/useHasAccessToExamQuestions";

import { makeLinkToCourseExamQuestionsSessionWithState } from "features/classroom/routes/classroom/routes/course/routes/course-exam-questions/links";
import { ExamQuestionsSource } from "features/exam-questions/analytics";
import { ROUTES } from "routing/routes";

const MAX_QUESTIONS_WITH_UNLIMITED_ACCESS = 10;

export function useMakeLinkToRelatedExamQuestions({
  examQuestionsInAssignmentSections,
  courseId,
  source
}: {
  examQuestionsInAssignmentSections: string[];
  courseId: string;
  source: ExamQuestionsSource;
}) {
  const { hasUnlimitedQuestions, remainingQuestionsCount } =
    useHasAccessToExamQuestions();

  const maxNumberOfExamQuestions = hasUnlimitedQuestions
    ? MAX_QUESTIONS_WITH_UNLIMITED_ACCESS
    : remainingQuestionsCount;

  const numberOfExamQuestionsToStudy = Math.min(
    examQuestionsInAssignmentSections.length,
    maxNumberOfExamQuestions
  );

  const linkToRelatedExamQuestions = useMemo(() => {
    if (numberOfExamQuestionsToStudy === 0) {
      return {
        pathname:
          ROUTES.CLASSROOM.COURSE.EXAM_QUESTIONS.PRACTICE.TOPICS.buildPath({
            courseId
          }),
        state:
          ROUTES.CLASSROOM.COURSE.EXAM_QUESTIONS.PRACTICE.TOPICS.buildState({
            source
          })
      };
    }

    const randomisedQuestions = shuffle(
      examQuestionsInAssignmentSections
    ).slice(0, numberOfExamQuestionsToStudy);

    return makeLinkToCourseExamQuestionsSessionWithState({
      courseId,
      selectedQuestionIds: randomisedQuestions,
      source
    });
  }, [
    courseId,
    examQuestionsInAssignmentSections,
    numberOfExamQuestionsToStudy,
    source
  ]);

  return linkToRelatedExamQuestions;
}
